import { useState } from 'react';

import { useFormMutation, useSubmitStepMutation } from '@/api/setters';
import { Steps } from '@/api/types';
import { Options } from '@/components/ui/CheckboxGroup';
import { ConditionalRender } from '@/components/ui/ConditionalRender';
import { formMutationDto } from '@/dto/formMutationDto';
import { StepForm as StepFormNew } from '@/features/v2/hh/StepForm';
import { useAdditionalInfoContext } from '@/features/v2/lib/additionalInfo';
import { useStepper } from '@/features/v2/lib/stepper';
import { DAMAGE_RIGHTS_ASSIGNMENTS_NAME } from '@/features/v2/vehicle-damage/components/CheckboxGroupDamageRightsAssignments';
import {
  REPAIR_DOCUMENTS_PROVIDED_TO_IC,
  REPAIR_DOCUMENTS_PROVIDED_TO_IC_NAME,
} from '@/features/v2/vehicle-damage/components/CheckboxGroupRepairDocumentsProvided';
import {
  BUSINESS_ACTIVITY,
  BUSINESS_ACTIVITY_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupBusinessActivity';
import {
  HAS_DAMAGE_PHOTOS,
  HAS_DAMAGE_PHOTOS_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupHasDamagePhotos';
import {
  VAT_PAYER_STATUS,
  VAT_PAYER_STATUS_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupVATTaxpayerStatus';
import { VEHICLE_AS_FIXED_ASSET_NAME } from '@/features/v2/vehicle-damage/components/RadioGroupVehicleAsFixedAsset';
import {
  VEHICLE_USED_FOR_BUSINESS,
  VEHICLE_USED_FOR_BUSINESS_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupVehicleUsedForBusiness';
import { VAT_DEDUCTION_TYPE_NAME } from '@/features/v2/vehicle-damage/components/RadioVATDeductionPercentage';
import { BUSINESS_TYPE_NAME } from '@/features/v2/vehicle-damage/components/SelectBusinessType';
import { StepForm } from '@/features/v2/vehicle-damage/StepForm';
import { VehicleDamageSchema } from '@/features/v2/vehicle-damage/stepsSchema';
import { Documents } from '@/features/v2/vehicle-damage/surveys/Documents/Documents';
import SurveyFour from '@/features/v2/vehicle-damage/surveys/SurveyFour';
import { SurveyOne } from '@/features/v2/vehicle-damage/surveys/SurveyOne';
import { SurveyOnePartThree } from '@/features/v2/vehicle-damage/surveys/SurveyOnePartThree';
import { SurveyOnePartTwo } from '@/features/v2/vehicle-damage/surveys/SurveyOnePartTwo';
import { SurveyThree } from '@/features/v2/vehicle-damage/surveys/SurveyThree';
import { SurveyTwo } from '@/features/v2/vehicle-damage/surveys/SurveyTwo/SurveyTwo';
import { scrollToTop } from '@/helpers/scrollToTop';
import cn from '@/utils/cn';

import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

export const Step1 = () => {
  const { handleSubmit } = useFormContext();
  const {
    step,
    innerStep,
    innerObject,
    nextInnerStep,
    stepTag,
    setStepTag,
    isDeadEnd,
  } = useStepper();
  const { mutate } = useFormMutation();
  const methods = useFormContext();
  const { watch } = useFormContext();
  const {
    setWithDamagePhotosRequired,
    setWithInvoicesReceiptsOrOtherDocumentsRequired,
  } = useAdditionalInfoContext();

  const lastStep = VehicleDamageSchema[step]?.lastStep?.(watch());

  const { i18n } = useLingui();
  const submitStep = useSubmitStepMutation();

  const isSurveyOnePartThree = step === 0 && innerStep >= 4;

  const [firstRender, setFirstRender] = useState(true);

  // TODO: remove this after 2/4 weeks from the release, temporary solution for old cases
  const oldCase = innerObject?.[0] < 3;

  return (
    <StepFormNew
      backButton={!!VehicleDamageSchema[step]?.backButton?.(innerStep, watch())}
      withTrigger={true}
      onBackButtonClick={() => {
        setFirstRender(false);
      }}
      submitButton={{
        label: i18n.t('Przejdź dalej'),
        uniqTestId: 'step-step-1-submit',
      }}
      nextButton={{
        uniqTestId: 'step-step-1',
      }}
      prevButton={{
        uniqTestId: 'step-step-1-prev',
      }}
      isLastStep={innerStep >= lastStep?.lastStep}
      onSubmit={handleSubmit(() => {
        if (innerStep >= lastStep?.lastStep) {
          if (stepTag === Steps.DAMAGE_VERIFICATION) {
            submitStep.mutateAsync(Steps.DOCUMENTS).then(() => {
              setStepTag(Steps.DOCUMENTS);
            });
          }

          setWithDamagePhotosRequired(
            watch(HAS_DAMAGE_PHOTOS_NAME) === HAS_DAMAGE_PHOTOS.YES
          );
          setWithInvoicesReceiptsOrOtherDocumentsRequired(
            (watch(REPAIR_DOCUMENTS_PROVIDED_TO_IC_NAME) as Options[])?.some(
              (item: Options) =>
                item.value !== REPAIR_DOCUMENTS_PROVIDED_TO_IC.ANY
            ) ?? false
          );
          return;
        }
        !isDeadEnd && nextInnerStep();
        innerStep === 3 && !firstRender && methods.trigger();
        mutate({
          ...formMutationDto({
            form_data: methods.watch(),
            stepper: {
              step,
              innerStep: { ...innerObject, [step]: innerStep + 1 },
            },
          }),
        });
      })}
      newSubmitStage
    >
      {step <= 1 && (
        <>
          <div
            className={cn(
              'flex flex-col gap-y-5',
              isSurveyOnePartThree && 'hidden '
            )}
          >
            <SurveyOne />
            <SurveyTwo />
            {!oldCase && <SurveyOnePartTwo />}
          </div>
          <div
            className={cn(
              'flex flex-col gap-y-5',
              isSurveyOnePartThree ? 'flex' : 'hidden'
            )}
          >
            {!oldCase && <SurveyOnePartThree />}
          </div>
        </>
      )}
    </StepFormNew>
  );
};

export const Step2 = () => {
  const { handleSubmit } = useFormContext();

  const methods = useFormContext();
  const { innerStep, step, innerObject, nextInnerStep } = useStepper();
  const { mutate } = useFormMutation();

  const { watch } = useFormContext();

  const lastStep = VehicleDamageSchema[step]?.lastStep?.(watch()) ?? 0;

  return (
    <StepForm
      submitButton={{
        label: 'Gotowe, przechodzę dalej',
        uniqTestId: 'step-step-2-submit',
      }}
      nextButton={{
        uniqTestId: 'step-step-2',
      }}
      prevButton={{
        uniqTestId: 'step-step-2-prev',
      }}
      isLastStep={innerStep === lastStep.lastStep}
      onSubmit={handleSubmit(() => {
        if (innerStep === lastStep.lastStep) {
          return;
        }
        nextInnerStep();
        mutate({
          ...formMutationDto({
            form_data: methods.watch(),
            stepper: {
              step,
              innerStep: { ...innerObject, [step]: innerStep + 1 },
            },
          }),
        });
        scrollToTop();
      })}
    >
      {step === 1 && <Documents />}
    </StepForm>
  );
};

export const Step3 = () => {
  const methods = useFormContext();
  const { innerStep, nextInnerStep, step, innerObject, stepTag, setStepTag } =
    useStepper();
  const { mutate } = useFormMutation();

  const { watch } = useFormContext();

  const lastStep = VehicleDamageSchema[step]?.lastStep?.(watch()) ?? 0;

  const submitStep = useSubmitStepMutation();

  const isSurveyFourInit =
    !methods.watch(REPAIR_DOCUMENTS_PROVIDED_TO_IC_NAME) &&
    !methods.watch(DAMAGE_RIGHTS_ASSIGNMENTS_NAME) &&
    innerStep >= 1;

  return (
    <StepForm
      isLastStep={innerStep === lastStep.lastStep}
      handleSubmit={async () => {
        if (innerStep === lastStep.lastStep) {
          return;
        }
        nextInnerStep();
        mutate({
          ...formMutationDto({
            form_data: methods.watch(),
            stepper: {
              step,
              innerStep: { ...innerObject, [step]: innerStep + 1 },
            },
          }),
        });
        if (innerStep >= 0) {
          if (stepTag === Steps.ADDITIONAL_INFORMATION) {
            submitStep.mutateAsync(Steps.COMPLETING_DOCUMENTS).then(() => {
              setStepTag(Steps.COMPLETING_DOCUMENTS);
            });
          }
        }
        isSurveyFourInit && scrollToTop();
      }}
      onSubmit={methods.handleSubmit(() => {
        if (innerStep === lastStep.lastStep) {
          return;
        }
      })}
      submitButton={{
        uniqTestId: 'step-step-3-submit',
      }}
      nextButton={{
        uniqTestId: 'step-step-3',
      }}
      prevButton={{
        uniqTestId: 'step-step-3-prev',
      }}
    >
      {step > 1 && step < 4 && (
        <>
          <SurveyThree />
          <ConditionalRender when={isSurveyThreeComplete}>
            <SurveyFour />
          </ConditionalRender>
        </>
      )}
    </StepForm>
  );
};

const isSurveyThreeComplete = (val: Record<string, unknown>) => {
  const businessActivity = val[BUSINESS_ACTIVITY_NAME];

  if (businessActivity === BUSINESS_ACTIVITY.NO) {
    return true;
  }

  if (businessActivity !== BUSINESS_ACTIVITY.YES) {
    return false;
  }

  const vehicleUsedForBusiness = val[VEHICLE_USED_FOR_BUSINESS_NAME];

  if (vehicleUsedForBusiness === VEHICLE_USED_FOR_BUSINESS.NO) {
    return true;
  }

  if (vehicleUsedForBusiness !== VEHICLE_USED_FOR_BUSINESS.YES) {
    return false;
  }

  if (!val[BUSINESS_TYPE_NAME]) {
    return false;
  }

  if (!val[VEHICLE_AS_FIXED_ASSET_NAME]) {
    return false;
  }

  const vatPayerStatus = val[VAT_PAYER_STATUS_NAME];

  if (!vatPayerStatus) {
    return false;
  }

  if (vatPayerStatus === VAT_PAYER_STATUS.NO) {
    return true;
  }

  return !!val[VAT_DEDUCTION_TYPE_NAME];
};
